import { useState, useRef, useEffect, type KeyboardEvent, type ClipboardEvent, type ChangeEvent } from "react";

import { PasswordResetInputTemplate } from "./password-reset-input.template";

export interface PasswordResetInputProps {
	readonly onChange?: (code: string) => void;
	readonly error?: boolean;
	readonly helperText?: string;
}

export const PasswordResetInput: React.FC<PasswordResetInputProps> = ({ onChange, error, helperText }) => {
	const [verificationCode, setVerificationCode] = useState<string[]>(["", "", "", "", "", ""]);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		onChange?.(verificationCode.join(""));
	}, [verificationCode, onChange]);

	const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		if (value.length > 1) {
			return;
		}

		const newCode = [...verificationCode];
		newCode[index] = value.toUpperCase();
		setVerificationCode(newCode);

		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && verificationCode[index] === "" && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").toUpperCase().slice(0, 6);
		const newCode = ["", "", "", "", "", ""];

		for (let i = 0; i < pastedData.length && i < 6; i++) {
			newCode[i] = pastedData[i];
		}
		setVerificationCode(newCode);

		const nextEmptyIndex = newCode.findIndex((val) => val === "");
		const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
		inputRefs.current[focusIndex]?.focus();
	};

	return (
		<PasswordResetInputTemplate
			verificationCode={verificationCode}
			inputRefs={inputRefs}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
			error={error}
			helperText={helperText}
		/>
	);
};
